import { useCallback, useMemo, useState } from 'react';
import { submitForm } from './helpers';
import { useDomainName } from './hooks';

// Regex taken from api-cp-lander
const validations = [
  {
    key: 'name',
    pattern: "^[\u0080-\uFFFFa-zA-Z0-9\\-.,\\(\\)\\@&' ]*$",
    message: 'Please enter a valid name',
    required: true,
    max: 30
  },
  {
    key: 'email',
    pattern: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$', // This one is from https://stackoverflow.com/a/742588
    message: 'Please enter a valid email',
    required: true
  },
  {
    key: 'phone',
    pattern: '^[0-9+.\\-x ]+$',
    message: 'Please enter a valid phone number',
    max: 25,
    required: true
  },
  { key: 'comments', max: 300 },
  { key: 'recaptcha', required: true }
];

const formValidator = (fields) => {
  const errs = [];
  validations.forEach((v) => {
    const label = fields[v.key].label;
    const formValue = fields[v.key].value;
    const match = formValue.match(v.pattern);
    if (v.pattern && (!match || match.length <= 0)) return errs.push(v.message);
    if (v.required && formValue.length <= 0) return errs.push(`${label} is required`);
    if (v.max && formValue.length > v.max) return errs.push(`${label} should be less then ${v.max} characters`);
  });
  return errs;
};

export const FORM_STATE = {
  INITIAL: 'INITAL',
  LOADING: 'LOADING',
  USER_FAILURE: 'USER_FAILURE',
  SERVER_FAILURE: 'SERVER_FAILURE',
  SUCCESS: 'SUCCESS'
};

export const useForm = () => {
  const [formState, setFormState] = useState(FORM_STATE.INITIAL);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', comments: '', recaptcha: '' });
  const [validationErrors, setValidationErrors] = useState([]);
  const domainName = useDomainName();

  const createSetter = useCallback(
    (fieldToSet) => {
      return (newValue) =>
        setFormData((prevForm) => {
          const copy = { ...prevForm };
          copy[fieldToSet] = newValue;
          return copy;
        });
    },
    [setFormData]
  );

  const fields = useMemo(
    () => ({
      name: {
        label: 'Name',
        value: formData.name,
        handleChange: createSetter('name')
      },
      email: {
        label: 'Email',
        value: formData.email,
        handleChange: createSetter('email')
      },
      phone: {
        label: 'Phone',
        value: formData.phone,
        handleChange: createSetter('phone')
      },
      comments: {
        label: 'Comments',
        value: formData.comments,
        handleChange: createSetter('comments')
      },
      recaptcha: {
        label: 'Recaptcha check',
        value: formData.recaptcha,
        handleChange: createSetter('recaptcha')
      }
    }),
    [createSetter, formData.comments, formData.email, formData.name, formData.phone, formData.recaptcha]
  );

  const submit = useCallback(async () => {
    const formErrors = formValidator(fields);
    if (formErrors.length > 0) {
      setValidationErrors(formErrors);
      return;
    }
    setFormState(FORM_STATE.LOADING);
    try {
      const res = await submitForm(domainName, formData);
      if (!res) setFormState(FORM_STATE.SUCCESS);
      else setFormState(FORM_STATE.USER_FAILURE);
      setValidationErrors([res]);
    } catch (e) {
      setFormState(FORM_STATE.SERVER_FAILURE);
      setValidationErrors(['Internal Server Error, Please try again']);
    }
  }, [domainName, fields, formData]);

  return {
    formState: formState,
    fields: fields,
    validationMessages: validationErrors,
    submit: submit
  };
};
